import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledCountryField } from '@/common/components/Fields/ControlledCountryField/ControlledCountryField';
import { ControlledTextField } from '@/common/components/Fields/ControlledTextField/ControlledTextField';
import { Form } from '@/common/components/Form/Form';
import { Grid } from '@/common/components/Grid/Grid';
import { Stack } from '@/common/components/Stack/Stack';
import {
  TCardShippingAddressStepSchema,
  cardShippingAddressStepSchema,
  limitLength,
} from '@/common/containers/Flow/Profile/Card/CardRequest/Steps/CardShippingAddress/CardShippingAddressStep.schema';
import { FlowLayout } from '@/common/layouts/FlowLayout/FlowLayout';
import { useFlowLayout } from '@/common/layouts/FlowLayout/FlowLayout.context';
import { IAddress } from '@/common/types/general';
import { TCardConfiguration } from '@/common/utils/card';
import { useCountries } from '@/web/hooks/useCountries';
import { zodResolver } from '@hookform/resolvers/zod';

type TCardShippingAddressStepProps = {
  initialAddress?: IAddress;
  setAddress: (address: IAddress) => void;
  onBack?: (address: IAddress) => void;
  config?: Pick<
    TCardConfiguration,
    'restrictSpecialCharacters' | 'maxAllowedCharacters' | 'requiresDistrict'
  >;
  suggestedCountry?: string;
  digitalCardId?: string;
};

export const CardShippingAddressStep = ({
  initialAddress,
  setAddress,
  onBack,
  config: {
    restrictSpecialCharacters = false,
    maxAllowedCharacters,
    requiresDistrict = false,
  } = {
    restrictSpecialCharacters: false,
    requiresDistrict: false,
  },
  suggestedCountry,
}: TCardShippingAddressStepProps) => {
  const { t } = useTranslation();
  const { goNext, goBack } = useFlowLayout();

  const { getCountryByCode } = useCountries();
  const initialCountry = getCountryByCode(
    initialAddress?.country || suggestedCountry
  );

  const methods = useForm<TCardShippingAddressStepSchema>({
    mode: 'onChange',
    defaultValues: {
      addressLine1: initialAddress?.addressLine1 ?? '',
      addressLine2: initialAddress?.addressLine2 ?? '',
      zipCode: initialAddress?.zipCode,
      city: initialAddress?.city,
      district: initialAddress?.district ?? '',
      country: initialCountry,
    },
    resolver: zodResolver(
      cardShippingAddressStepSchema({
        restrictSpecialCharacters,
        requiresDistrict,
      })
    ),
  });

  const { formState, handleSubmit } = methods;

  const mapFormDataToAddress = (formData: TCardShippingAddressStepSchema) => {
    return {
      addressLine1: formData.addressLine1,
      addressLine2: formData.addressLine2,
      zipCode: formData.zipCode,
      city: formData.city,
      district: formData.district,
      country: formData.country?.iso2,
    };
  };

  return (
    <FlowLayout.Content
      primaryAction={{
        children: t('general.navigation.next'),
        loading: formState.isSubmitting,
        onPress: handleSubmit((data) => {
          setAddress(mapFormDataToAddress(data));
          goNext();
        }),
      }}
      secondaryAction={
        onBack && {
          children: t('general.navigation.back'),
          disabled: formState.isSubmitting,
          onPress: () => {
            onBack(mapFormDataToAddress(methods.getValues()));
            goBack();
          },
        }
      }
    >
      <FormProvider {...methods}>
        <Form>
          <Stack gap="form">
            <ControlledTextField
              name="addressLine1"
              label={t(
                'dashboard.request_card.form.fields.street_and_number.label'
              )}
              maxLength={maxAllowedCharacters}
            />
            <ControlledTextField
              name="addressLine2"
              label={`${t(
                'dashboard.request_card.form.fields.other_address_details.label'
              )} ${t('form.fields.optional')}`}
              hint={t(
                'dashboard.request_card.form.fields.other_address_details.hint'
              )}
              maxLength={maxAllowedCharacters}
            />
          </Stack>
          <Grid
            gap="form"
            className="mt-sm"
            columnBasis="basis-[24rem]"
            maxColumns={2}
          >
            <ControlledTextField
              name="zipCode"
              label={t('dashboard.request_card.form.fields.zip_code.label')}
              inputFormatter={limitLength(10)}
            />
            <ControlledTextField
              name="city"
              label={t('dashboard.request_card.form.fields.city.label')}
              inputFormatter={limitLength(40)}
            />
            {requiresDistrict && (
              <ControlledTextField
                name="district"
                label={t('dashboard.request_card.form.fields.district.label')}
                inputFormatter={limitLength(32)}
              />
            )}
            <ControlledCountryField
              name="country"
              label={t('dashboard.request_card.form.fields.country.label')}
            />
          </Grid>
        </Form>
      </FormProvider>
    </FlowLayout.Content>
  );
};
